import { datadogRum } from '@datadog/browser-rum';
import { Button, Stack } from '@mui/material';
import { Show, useIsMobile } from '@side/pantry';
import { useRouter } from 'next/router';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useEffectOnce } from 'react-use';
import type { ReferralTypeValue } from '@/common/hooks/referrals/types';
import { useCreateReferral } from '@/common/hooks/referrals/useCreateReferral';
import { useAgentDslFilters } from '@/common/hooks/useAgentDslFilters';
import { useRouteParam } from '@/common/hooks/useRouteParam';
import { useSubmitAsTeamMembers } from '@/common/hooks/useSubmitAsTeamMembers';
import { doesAgentHaveRequiredFields } from '@/common/utils/agents';
import {
  formatTeamMemberId,
  formatTeamMemberIds,
} from '@/common/utils/formatTeamMemberId';
import { UnsavedChangesContext } from '@/components/UnsavedChanges';
import { TXM_REFERRALS_PATH } from '@/constants';
import { Form } from '@/views/NewInteraction/Form';
import { FormContainer } from '@/views/NewInteraction/FormContainer';
import { FormHeader } from '@/views/NewInteraction/FormHeader';
import {
  TrackReferralsEvent,
  useTrackReferralsAnalytics,
} from '$analytics/useTrackAnalytics/domains/referrals';
import { useDefaultPrimaryAgent } from '$form-fields/AgentAutocomplete/AgentAutocomplete.helpers';
import {
  REFERRAL_CONTEXT_DEFAULTS,
  ReferralContext,
} from '../../common/ReferralContext';
import { AgentDetails } from '../../components/AgentDetails/AgentDetails';
import { ClientDetails } from '../../components/ClientDetails/ClientDetails';
import { useAgentsWithSelections } from '../../hooks/useAgentsWithSelections';
import { getFirstAgentFieldName, getIsSending } from '../../utils';
import { ButtonContainer } from './ButtonContainer';
import {
  buildReferralPostBody,
  useDefaultTransactingAgent,
} from './NewReferral.utils';
import { defaultValues, type FieldValues } from './types';

const validReferralTypes: ReferralTypeValue[] = ['sending', 'receiving'];
const isValidReferralType = (value: string): value is ReferralTypeValue =>
  validReferralTypes.includes(value as ReferralTypeValue);

export function NewReferral() {
  const trackEvent = useTrackReferralsAnalytics();
  const isMobile = useIsMobile();
  const router = useRouter();
  const { setIsPageDirty } = useContext(UnsavedChangesContext);
  const [createReferral] = useCreateReferral();
  const referralTypeQueryParam = useRouteParam('referralType', '');
  const {
    loading: defaultTransactingAgentLoading,
    agent: defaultTransactingAgent,
  } = useDefaultTransactingAgent();
  const { loading: defaultAgentLoading, defaultAgent } =
    useDefaultPrimaryAgent();
  const firstAgentInitialized = useRef(false);
  const transactingAgentInitialized = useRef(false);

  const methods = useForm<FieldValues>({
    defaultValues: {
      ...defaultValues,
      referralType: isValidReferralType(referralTypeQueryParam)
        ? referralTypeQueryParam
        : 'sending',
    },
    mode: 'all',
  });

  const {
    control,
    formState: { isDirty, isSubmitting, isValid: formIsValid },
    setValue,
    handleSubmit,
  } = methods;

  const [referralType, referringAgent, transactingAgent] = useWatch({
    control,
    name: ['referralType', 'referringAgent', 'transactingAgent'],
  });

  useEffectOnce(() => {
    trackEvent(TrackReferralsEvent.AddReferralLanding);
  });

  useEffect(() => {
    setIsPageDirty(isDirty);
  }, [isDirty, setIsPageDirty]);

  const agentDslFilters = useAgentDslFilters();
  const sendingDslFilters = useAgentDslFilters({
    roles: ['AGENT', 'REFERRAL_AGENT'],
  });

  const { data: submitAsAgentsData, loading: submitAsAgentsLoading } =
    useSubmitAsTeamMembers({
      filters: referralType === 'sending' ? sendingDslFilters : agentDslFilters,
    });
  const submitAsAgentsBase = useMemo(
    () => (submitAsAgentsData ?? []).filter(doesAgentHaveRequiredFields),
    [submitAsAgentsData],
  );
  const { agents: submitAsAgents, registerSelectedAgent } =
    useAgentsWithSelections(submitAsAgentsBase, {
      referringAgent,
      transactingAgent,
    });

  const isSending = getIsSending({ referralType });
  const firstAgentFieldName = getFirstAgentFieldName({ referralType });

  useEffect(() => {
    if (defaultAgent && !firstAgentInitialized.current) {
      setValue(`${firstAgentFieldName}.teamId`, defaultAgent.teamIdentityId);
      setValue(`${firstAgentFieldName}.userId`, defaultAgent.userIdentityId);

      firstAgentInitialized.current = true;
    }
  }, [defaultAgent, firstAgentFieldName, setValue]);

  useEffect(() => {
    if (
      isSending &&
      defaultTransactingAgent &&
      !transactingAgentInitialized.current
    ) {
      setValue('transactingAgent', defaultTransactingAgent, {
        shouldValidate: true,
      });
      transactingAgentInitialized.current = true;
    }
  }, [
    defaultTransactingAgent,
    isSending,
    setValue,
    transactingAgentInitialized,
  ]);

  const loading =
    submitAsAgentsLoading ||
    defaultAgentLoading ||
    defaultTransactingAgentLoading;

  const selectedAgent = useMemo(() => {
    const agentToFind = isSending ? referringAgent : transactingAgent;
    if (!agentToFind) return undefined;

    return submitAsAgents.find((agent) =>
      formatTeamMemberIds(agent).includes(
        formatTeamMemberId(agentToFind.userId ?? '', agentToFind.teamId ?? ''),
      ),
    );
  }, [isSending, referringAgent, submitAsAgents, transactingAgent]);

  const isValid = formIsValid && Boolean(selectedAgent);

  const contextValue = useMemo(
    () => ({
      ...REFERRAL_CONTEXT_DEFAULTS,
      loading,
      submitAsAgents,
      submitAsAgentsBase,
      registerSelectedAgent,
    }),
    [loading, submitAsAgents, submitAsAgentsBase, registerSelectedAgent],
  );

  const submit = async (formData: FieldValues) => {
    if (!formData.referringAgent || !formData.transactingAgent) {
      datadogRum.addError(
        new Error('Referral submitted without both agents populated'),
        {
          hasReferringAgent: !!formData.referringAgent,
          hasTransactingAgent: !!formData.transactingAgent,
          referralType: formData.referralType,
        },
      );
      return;
    }

    setIsPageDirty(false);

    const body = buildReferralPostBody(formData);
    if (!body) {
      return;
    }

    const { data, error } = await createReferral(body);

    if (error) {
      trackEvent(TrackReferralsEvent.CreateReferral, {
        referralType: isSending ? 'sending' : 'receiving',
        success: false,
      });
      setIsPageDirty(true);
      return;
    }

    trackEvent(TrackReferralsEvent.CreateReferral, {
      interactionId: data?.id,
      success: true,
      contactId: data?.owner,
      referralType: isSending ? 'sending' : 'receiving',
    });
  };

  const handleCancel = () => {
    void router.push(TXM_REFERRALS_PATH);
  };

  return (
    <ReferralContext.Provider value={contextValue}>
      <FormProvider {...methods}>
        <FormContainer>
          <Form as="div" sx={{ pb: { xs: 5, sm: 10 } }}>
            <FormHeader
              headerText="Create a New Client Referral"
              subheaderText="Add details about the referral and client"
            />

            <Stack
              sx={{
                gap: { xs: 2.5, md: 3.75 },
                justifyContent: 'space-between',
              }}
            >
              <AgentDetails />

              <ClientDetails />

              <Show when={!isMobile}>
                <ButtonContainer>
                  <Button
                    disabled={isSubmitting}
                    onClick={handleCancel}
                    variant="outlined"
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={!isValid}
                    loading={isSubmitting}
                    onClick={() => void handleSubmit(submit)()}
                    variant="contained"
                  >
                    Create Referral & Start Agreement
                  </Button>
                </ButtonContainer>
              </Show>
            </Stack>
          </Form>
        </FormContainer>

        <Show when={isMobile}>
          <ButtonContainer>
            <Button
              disabled={isSubmitting}
              onClick={handleCancel}
              variant="outlined"
              sx={{ flexBasis: '100%' }}
            >
              Cancel
            </Button>
            <Button
              disabled={!isValid}
              loading={isSubmitting}
              onClick={() => void handleSubmit(submit)()}
              variant="contained"
              sx={{ flexBasis: '100%' }}
            >
              Create Referral
            </Button>
          </ButtonContainer>
        </Show>
      </FormProvider>
    </ReferralContext.Provider>
  );
}
